import is from 'is_js';
import invariant from 'invariant';
import { List } from 'immutable';
import { formatPattern } from 'react-router';
import { log } from 'omni-common-ui';

const ALWAYS_APPLIED_PARAMS = { mode: '' };
export const NAV_TAB_DESCRIPTOR_SYMB = Symbol('TabNavBuilder#NAV_TAB_DESCRIPTOR');

export const TabNavBuilder = {
  convertNavItemToTabItem: (navItem, routes) => ({
    ...navItem,
    text: navItem.label,
    isActive: (() => {
      const deepestRouteWithGroupNavTab =
          routes.concat().reverse().find((route) => is.object(route.tab));
      return is.object(deepestRouteWithGroupNavTab) &&
          deepestRouteWithGroupNavTab.tab === navItem[NAV_TAB_DESCRIPTOR_SYMB];
    })(),
  }),
  buildTabItemsForRoutes: (routes, baseParams) =>
      TabNavBuilder.buildNavItemsForRoutes(routes, baseParams)
          .map((descriptor) => TabNavBuilder.convertNavItemToTabItem(descriptor, routes)),
  buildNavItemsForRoutes: (routes, baseParams = {}, opts = {}) => {
    invariant(is.array(routes), 'routes must be an array');
    invariant(is.object(baseParams), 'baseParams must be an object');

    const buildRouteHref = (routeSegments, params = {}) => {
      const paths = routeSegments
        .filter((route) => is.string(route.path))
        .map((route) => route.path);

      // ensure that paths start with a '/'
      let path = paths.join('/');
      if (path.charAt(0) !== '/') {
        path = `/${path}`;
      }

      try {
        return formatPattern(path, {
          ...ALWAYS_APPLIED_PARAMS,
          ...params,
        }).replace(/(.+)\/$/, '$1');
      } catch (e) {
        log.warn('Unable to format path pattern for GroupNav item. Returning null link href.', e);
        return null;
      }
    };

    const findGroupNavTabDescriptors = (currentRoute, path = [], descriptors = []) => {
      const currentPath = path.concat([currentRoute]);

      if (is.object(currentRoute) && is.object(currentRoute.tab)) {
        const { tab } = currentRoute;

        // tab sanity checks
        invariant(is.undefined(tab.params) || is.function(tab.params),
            'tab.params must be undefined or a function');

        let tabLabel = '';
        if (is.string(tab.label)) {
          tabLabel = tab.label;
        } else if (is.function(tab.label)) {
          tabLabel = tab.label(baseParams);
        }
        const isItemVisible = is.not.function(tab.isVisible) || tab.isVisible(baseParams);

        if (isItemVisible || opts.disableInsteadOfHide) {
          const paramsFn = tab.params || ((params) => params);
          const queryFn = tab.query || (() => null);
          const nowParams = { ...baseParams };
          const href = buildRouteHref(currentPath, paramsFn(nowParams));
          const query = queryFn(nowParams);

          descriptors.push({
            [NAV_TAB_DESCRIPTOR_SYMB]: tab,  // used in convertNavItemToTabItem
            label: tabLabel,
            order: tab.order,
            href: is.null(href) ? null : href + (is.string(query) ? `?${query}` : ''),
            isDisabled: is.null(href) || ((!! opts.disableInsteadOfHide) && ! isItemVisible),
          });
        }
      }

      if (is.object(currentRoute) && is.array(currentRoute.childRoutes)) {
        currentRoute.childRoutes.forEach((route) =>
          findGroupNavTabDescriptors(route, currentPath, descriptors));
      }

      return new List(descriptors)
        .sortBy((d) => d.order)
        .toArray();
    };

    const firstTabConfigIndex = routes.findIndex((routeItem) => is.object(routeItem.tab));
    return findGroupNavTabDescriptors(routes[firstTabConfigIndex],
        routes.slice(0, firstTabConfigIndex));
  },
};

export default TabNavBuilder;



// WEBPACK FOOTER //
// ./domain/TabNavBuilder/index.js